import React from 'react'
import isString from 'lodash/isString'
import { useAuth } from 'oidc-react'

import { AuthFields, formatUserPrivileges } from 'Shared/utils/auth'

import { UserRole } from 'Portals/Internal/constants'

type VisibleForProps<Permission, UserTypes> = {
	permissions: Permission[] // if it is an empty array, component will always be visible
	fallback?: JSX.Element
	userTypes?: UserTypes[]
	userRoles?: UserRole[]
	children?: string | JSX.Element | JSX.Element[]
	excludeTurkishUsers?: boolean
	hidden?: boolean
}

function VisibleFor<Permission, UserTypes>({
	userTypes = [],
	userRoles = [],
	permissions,
	children,
	fallback,
	excludeTurkishUsers = false,
	hidden = false
}: VisibleForProps<Permission, UserTypes>) {
	const auth = useAuth()
	const userPermissions = formatUserPrivileges(auth.userData) as Permission[]
	const userType: UserTypes | null =
		(auth.userData?.profile[AuthFields.userType] as UserTypes) || null

	const isTurkishUser =
		auth.userData?.profile[AuthFields.hasTurkishBillingAgency] === 'True' || false

	const isVisibleByPermissions =
		permissions.length === 0 || userPermissions.some((p) => permissions.includes(p))
	const isVisibleByUserType = userTypes.length === 0 || userTypes.some((p) => userType === p)

	const authUserRolesData = auth.userData?.profile[AuthFields.userRole] as UserRole[] | UserRole
	const authUserRoles = isString(authUserRolesData) ? [authUserRolesData] : authUserRolesData
	const isVisibleByRole =
		userRoles.length === 0 || authUserRoles.some((role) => userRoles?.includes(role))

	if (
		isVisibleByPermissions &&
		isVisibleByUserType &&
		isVisibleByRole &&
		!hidden &&
		(excludeTurkishUsers ? !isTurkishUser : true)
	) {
		return <>{children}</>
	}

	return fallback || null
}

export default VisibleFor
